// The estimate-history chart: four series on ONE minutes axis (displayed,
// raw, minimum, offset — offset is minutes too, so no second scale).
// Recharts line chart per the dataviz method: 2px lines, identity carried
// by color AND dash/step shape, recessive grid, legend + crosshair tooltip,
// text in text tokens. Series colors come from the validated --chart-*
// tokens in app.css.

import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ReferenceArea,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

export interface EstimateChartPoint {
    /** Milliseconds since epoch. */
    at: number;
    displayed: number;
    raw: number | null;
    minimum: number;
    offset: number;
    samples: number;
}

export interface EstimateChartProps {
    points: EstimateChartPoint[];
    /** Service outages over the same window, shaded across the series. */
    outages?: ChartOutage[];
    formatTime: (at: number) => string;
    /** Fixed size for tests; production uses the responsive container. */
    width?: number;
    height?: number;
}

const SERIES: Record<string, { label: string; color: string }> = {
    displayed: { label: "Displayed", color: "var(--chart-displayed)" },
    raw: { label: "Raw", color: "var(--chart-raw)" },
    minimum: { label: "Minimum", color: "var(--color-muted-foreground)" },
    offset: { label: "Offset", color: "var(--chart-offset)" },
};

interface TooltipRow {
    dataKey?: string | number;
    value?: number | string | null;
    payload?: EstimateChartPoint;
}

function ChartTooltip({
    active,
    payload,
    label,
    formatTime,
}: {
    active?: boolean;
    payload?: TooltipRow[];
    label?: number;
    formatTime: (at: number) => string;
}) {
    if (!active || !payload || payload.length === 0 || typeof label !== "number") return null;
    const point = payload[0]?.payload;
    return (
        <div className="rounded-md border border-border bg-popover px-3 py-2 text-xs shadow-sm">
            <p className="mb-1 font-medium text-foreground">{formatTime(label)}</p>
            {payload.map((row) => {
                const key = String(row.dataKey);
                const series = SERIES[key];
                if (!series) return null;
                return (
                    <p key={key} className="flex items-center gap-2 text-muted-foreground">
                        <span
                            aria-hidden="true"
                            className="inline-block size-2 rounded-full"
                            style={{ backgroundColor: series.color }}
                        />
                        {series.label}: {row.value === null || row.value === undefined ? "—" : `${row.value} min`}
                    </p>
                );
            })}
            {point && <p className="mt-1 text-muted-foreground">Samples: {point.samples}</p>}
        </div>
    );
}

/** An service outage, clipped to the chart's own domain. `to === null` means
 *  the service is still away, so the band runs to the newest point. */
export interface ChartOutage {
    from: number;
    to: number | null;
}

/** Outage bands, clipped to the drawn domain.
 *
 * An outage is recorded by the worker from the service's heartbeat, so it is
 * known independently of the data — which is the whole point. During an outage
 * shorter than the screen's cutoff window the estimate keeps averaging orders
 * already in hand and `raw` never goes null, so nothing in the series itself
 * betrays the gap. That is exactly the stretch an operator needs marked. */
export function outageBands(points: EstimateChartPoint[], outages: ChartOutage[]): { from: number; to: number }[] {
    const first = points[0];
    const last = points[points.length - 1];
    if (!first || !last) return [];
    const bands: { from: number; to: number }[] = [];
    for (const outage of outages) {
        const from = Math.max(outage.from, first.at);
        const to = Math.min(outage.to ?? last.at, last.at);
        if (to > from) bands.push({ from, to });
    }
    return bands;
}

export function EstimateChart({ points, outages = [], formatTime, width, height }: EstimateChartProps) {
    if (points.length === 0) return null;
    const hasNegative = points.some((p) => p.offset < 0 || p.displayed < 0);
    const chartHeight = height ?? 280;

    const chart = (
        <LineChart data={points} width={width} height={width ? chartHeight : undefined}>
            <CartesianGrid stroke="var(--color-border)" vertical={false} />
            <XAxis
                dataKey="at"
                type="number"
                scale="time"
                domain={["dataMin", "dataMax"]}
                tickFormatter={formatTime}
                tick={{ fill: "var(--color-muted-foreground)", fontSize: 11 }}
                tickLine={false}
                axisLine={{ stroke: "var(--color-border)" }}
                minTickGap={48}
            />
            <YAxis
                width={36}
                tick={{ fill: "var(--color-muted-foreground)", fontSize: 11 }}
                tickLine={false}
                axisLine={false}
            />
            <Tooltip
                cursor={{ stroke: "var(--color-border)" }}
                content={<ChartTooltip formatTime={formatTime} />}
                isAnimationActive={false}
            />
            <Legend
                formatter={(value: string) => (
                    <span className="text-xs text-muted-foreground">{SERIES[value]?.label ?? value}</span>
                )}
            />
            {/* The one band on this chart, so it can be plain: a shaded
                period with a dashed edge, meaning "the posting service was away". */}
            {outageBands(points, outages).map((band) => (
                <ReferenceArea
                    key={`outage-${band.from}`}
                    x1={band.from}
                    x2={band.to}
                    fill="var(--color-muted)"
                    fillOpacity={0.7}
                    stroke="var(--color-muted-foreground)"
                    strokeOpacity={0.5}
                    strokeDasharray="3 3"
                />
            ))}
            {hasNegative && <ReferenceLine y={0} stroke="var(--color-border)" />}
            <Line
                dataKey="minimum"
                stroke={SERIES.minimum?.color}
                strokeWidth={1}
                strokeDasharray="2 4"
                dot={false}
                isAnimationActive={false}
            />
            <Line
                dataKey="raw"
                stroke={SERIES.raw?.color}
                strokeWidth={2}
                strokeDasharray="6 4"
                connectNulls={false}
                dot={false}
                activeDot={{ r: 4 }}
                isAnimationActive={false}
            />
            <Line
                dataKey="offset"
                type="stepAfter"
                stroke={SERIES.offset?.color}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
                isAnimationActive={false}
            />
            <Line
                dataKey="displayed"
                stroke={SERIES.displayed?.color}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
                isAnimationActive={false}
            />
        </LineChart>
    );

    if (width) return chart;
    return (
        <ResponsiveContainer width="100%" height={chartHeight}>
            {chart}
        </ResponsiveContainer>
    );
}
